


import React, { Suspense, useCallback, useMemo, useState, useEffect } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { FeatureGrid } from './components/FeatureGrid.tsx';
import { FeaturePalette } from './components/FeaturePalette.tsx';
import { useGlobalState } from './contexts/GlobalStateContext.tsx';
import { logEvent } from './services/telemetryService.ts';
import { ALL_FEATURES, FEATURES_MAP } from './components/features/index.ts';
import type { ChromeViewType, FeatureId, ViewType } from './types.ts';
import { generateImage } from './services/geminiService.ts';
import { DownloadManager } from './components/DownloadManager.tsx';

const viewComponentMap: Record<ChromeViewType, React.FC<any>> = {
    'features-list': FeatureGrid,
};

const LoadingIndicator: React.FC = () => (
    <div className="w-full h-full flex items-center justify-center">
        <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0s' }}></div>
            <div className="w-4 h-4 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-4 h-4 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            <span className="text-slate-400 ml-2">Loading Feature...</span>
        </div>
    </div>
);


const App: React.FC = () => {
  const { state, dispatch } = useGlobalState();
  const { activeView, viewProps } = state;
  const [bgImageUrl, setBgImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchBgImage = async () => {
        try {
            const imageUrl = await generateImage("ethereal abstract digital art, developer productivity theme, nodes and light, dark cinematic mood");
            setBgImageUrl(imageUrl);
        } catch (error) {
            console.error("Failed to generate background image:", error);
        }
    };
    fetchBgImage();
  }, []);

  const handleViewChange = useCallback((view: ViewType, props: any = {}) => {
    dispatch({ type: 'SET_VIEW', payload: { view, props } });
    logEvent('view_changed', { view });
  }, [dispatch]);

  const { ActiveComponent, props } = useMemo(() => {
    const feature = FEATURES_MAP.get(activeView as FeatureId);
    let componentProps: any = { onNavigate: handleViewChange, ...viewProps };

    if (feature?.component) {
      componentProps.feature = feature;
      return {
        ActiveComponent: feature.component,
        props: componentProps,
      };
    }
    
    const ViewComponent = viewComponentMap[activeView as ChromeViewType];
    const FinalComponent = ViewComponent || FeatureGrid;
    componentProps.features = ALL_FEATURES;
    componentProps.onFeatureSelect = handleViewChange;

    return {
        ActiveComponent: FinalComponent,
        props: componentProps
    };
  }, [activeView, handleViewChange, viewProps]);

  return (
    <div className="relative h-screen w-screen font-sans bg-slate-900 text-slate-300 overflow-hidden">
        {bgImageUrl && (
            <div 
                className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 animate-fadeIn" 
                style={{ backgroundImage: `url(${bgImageUrl})` }}
            >
                <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"></div>
            </div>
        )}
        <div className="relative flex h-full w-full fade-in">
            <FeaturePalette onFeatureSelect={handleViewChange} />
            <main className="relative flex-1 overflow-y-auto min-w-0">
                <ErrorBoundary>
                <Suspense fallback={<LoadingIndicator />}>
                    <ActiveComponent {...props} />
                </Suspense>
                </ErrorBoundary>
                <DownloadManager />
            </main>
        </div>
    </div>
  );
};

export default App;